import React from 'react';

interface PublicFormFieldProps {
  id: string;
  label: string;
  error?: string | null;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Labeled public form field with optional error and hint text.
 * Use when building Job Alerts / Post Jobs / similar intake forms so
 * placeholders are never the only accessible name.
 */
const PublicFormField: React.FC<PublicFormFieldProps> = ({
  id,
  label,
  error,
  hint,
  className = '',
  children,
}) => {
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`public-form-field ${className}`.trim()}>
      <label className="public-form-label" htmlFor={id}>
        {label}
      </label>
      {React.isValidElement(children)
        ? React.cloneElement(
            children as React.ReactElement<{
              id?: string;
              'aria-invalid'?: boolean;
              'aria-describedby'?: string;
            }>,
            {
              id,
              'aria-invalid': Boolean(error) || undefined,
              'aria-describedby': describedBy,
            }
          )
        : children}
      {hint && !error && (
        <p id={hintId} className="public-form-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="public-form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default PublicFormField;
